import React from "react";
import { Box, Typography, Skeleton, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useNflTopPlayers } from "../../hooks/useNflTopPlayers";
import { getNflAllTimeGreatsStat } from "../../constants/nflAllTimeGreats";
import PlayerHeadshot from "../PlayerHeadshot";
import HomeSection from "./HomeSection";
import { homeCardHover, horizontalScroll } from "./homeStyles";
import { colors } from "../../theme";
import { useNflLeagueScope } from "../../hooks/useNflLeagueScope";
import NflIncludeCflToggle from "../nfl/NflIncludeCflToggle";

const NflHotPlayerCard = ({ player, statField, statLabel, onClick }) => {
  const value = player[statField] ?? "–";
  const position = player.position;

  return (
    <Box
      onClick={onClick}
      sx={{
        minWidth: 140,
        maxWidth: 140,
        flex: "0 0 auto",
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${colors.border}`,
        ...homeCardHover,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
        <PlayerHeadshot
          playerId={player.playerId}
          sport="nfl"
          size={56}
          showTooltip={false}
          showLoading={false}
        />
      </Box>
      <Typography variant="body2" fontWeight={600} noWrap textAlign="center" title={player.name}>
        {player.name}
      </Typography>
      {position && (
        <Typography variant="caption" color="text.secondary" noWrap display="block" textAlign="center">
          {position}
        </Typography>
      )}
      <Box sx={{ mt: 1, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          {statLabel}
        </Typography>
        <Typography variant="h6" fontWeight={700} color="primary.main" lineHeight={1.2}>
          {Number(value).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

const NflHotPlayersRow = () => {
  const navigate = useNavigate();
  const category = "passing-yards";
  const statConfig = getNflAllTimeGreatsStat(category);
  const { leagueScope, includeCfl, setIncludeCfl } = useNflLeagueScope();
  const { topPlayers, isLoading, error } = useNflTopPlayers(category, leagueScope);

  return (
    <HomeSection
      title="All-time passing yards"
      subtitle="Tap a legend to open their profile"
      noPadding
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 1 }}>
          <NflIncludeCflToggle includeCfl={includeCfl} onToggle={setIncludeCfl} />
        </Box>
        {isLoading && (
          <Box sx={horizontalScroll}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" width={140} height={160} sx={{ flexShrink: 0 }} />
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="warning">Could not load top passers</Alert>
        )}

        {!isLoading && !error && topPlayers.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            No player data available
          </Typography>
        )}

        {!isLoading && !error && topPlayers.length > 0 && (
          <Box sx={horizontalScroll}>
            {topPlayers.map((player) => (
              <NflHotPlayerCard
                key={player.playerId}
                player={player}
                statField={statConfig.statField}
                statLabel={statConfig.railLabel}
                onClick={() => navigate(`/app/nfl/player/${player.playerId}`)}
              />
            ))}
          </Box>
        )}
      </Box>
    </HomeSection>
  );
};

export default NflHotPlayersRow;
